"use client";

import { PexLogo } from "@pex/design-tokens";
import { Button, Progress } from "@pex/ui-web";
import { ChevronLeft } from "lucide-react";
import type React from "react";

interface TypeformShellProps {
  children: React.ReactNode;
  // stretches content to fill available height — use when children manage their own internal scroll
  fullHeight?: boolean;
  onBack?: () => void;
  // undefined = hide the progress bar (e.g. on the group selection screen)
  progress?: number;
  // top-aligns content instead of centering — use for scrollable list screens
  scrollLayout?: boolean;
}

function innerClass(fullHeight?: boolean, scrollLayout?: boolean): string {
  if (fullHeight) {
    return "flex min-h-0 flex-1 flex-col py-8";
  }
  if (scrollLayout) {
    return "pt-0 pb-8";
  }
  return "my-auto py-8";
}

export function TypeformShell({
  progress,
  onBack,
  children,
  scrollLayout,
  fullHeight,
}: TypeformShellProps) {
  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      {progress !== undefined && (
        <Progress
          className="h-0.5 rounded-none [&_[data-slot=progress-indicator]]:bg-[#feb240]"
          value={progress}
        />
      )}
      <div className="flex items-center px-4 py-3">
        <PexLogo className="h-9 w-auto" />
      </div>
      <div
        className={`flex flex-1 flex-col items-center overscroll-none px-4 ${fullHeight ? "overflow-hidden" : "overflow-y-auto pb-[60px]"}`}
      >
        <div
          className={`w-full max-w-lg ${innerClass(fullHeight, scrollLayout)}`}
        >
          {onBack && (
            <Button
              aria-label="Go back"
              className="mb-4 -ml-1 h-9 w-9 shrink-0 p-0 text-muted-foreground"
              onClick={onBack}
              size="sm"
              type="button"
              variant="ghost"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}
          {children}
          {onBack && !fullHeight && (
            <div aria-hidden="true" className="h-[52px]" />
          )}
        </div>
      </div>
    </div>
  );
}
